import React, {
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  Form,
  Pagination,
  Dropdown,
  ButtonGroup,
} from 'react-bootstrap';
import debounce from 'lodash.debounce';
import ActivityIndicator from './ActivityIndicator';

const DataTable = forwardRef(({
  columns = [],
  onFetchData = async () => ({ data: [], pageCount: 0, totalItems: 0 }),
  rowIdAccessor = row => row.id,
  onRowClick,
  tableId = 'default',
  isLoading = false,
  hideSearch = false,
}, ref) => {
  const STORAGE_KEY = `datatable_column_visibility_${tableId}`;

  const [data, setData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(null); // ⬅️ NEW
  const [search, setSearch] = useState('');
  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const fetchData = async () => {
    const resp = await onFetchData({ pageIndex, pageSize, search });
    const rows = resp?.data ?? [];
    const serverPageCount = resp?.pageCount;
    const serverTotal = typeof resp?.totalItems === 'number' ? resp.totalItems : null;

    setData(rows);
    setTotalItems(serverTotal);

    // If server gives pageCount, respect it; else derive from totalItems when available
    if (typeof serverPageCount === 'number' && serverPageCount >= 0) {
      setPageCount(serverPageCount);
    } else if (typeof serverTotal === 'number') {
      setPageCount(Math.max(1, Math.ceil(serverTotal / pageSize)));
    } else {
      // Worst-case fallback: single page if unknown
      setPageCount(1);
    }
  };

  useImperativeHandle(ref, () => ({
    refetch: fetchData,
  }));

  const debouncedFetch = useMemo(
    () => debounce(fetchData, 800),
    // Recreate the debounced fn when these change
    [pageIndex, pageSize, search]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination: { pageIndex, pageSize },
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderPagination = () => {
    if (pageCount <= 1) return null;

    const pages = [];
    for (let i = 0; i < pageCount; i++) {
      if (i === 0 || i === pageCount - 1 || (i >= pageIndex - 2 && i <= pageIndex + 2)) {
        pages.push(
          <Pagination.Item key={i} active={i === pageIndex} onClick={() => setPageIndex(i)}>
            {i + 1}
          </Pagination.Item>
        );
      } else if (i === 1 && pageIndex > 3) {
        pages.push(<Pagination.Ellipsis key="start" disabled />);
      } else if (i === pageCount - 2 && pageIndex < pageCount - 4) {
        pages.push(<Pagination.Ellipsis key="end" disabled />);
      }
    }

    return (
      <Pagination className="mb-0">
        <Pagination.First onClick={() => setPageIndex(0)} disabled={pageIndex === 0} />
        <Pagination.Prev onClick={() => setPageIndex(prev => Math.max(prev - 1, 0))} disabled={pageIndex === 0} />
        {pages}
        <Pagination.Next onClick={() => setPageIndex(prev => Math.min(prev + 1, pageCount - 1))} disabled={pageIndex >= pageCount - 1} />
        <Pagination.Last onClick={() => setPageIndex(pageCount - 1)} disabled={pageIndex >= pageCount - 1} />
      </Pagination>
    );
  };

  // Range summary (Showing X–Y of Z)
  const startItem = totalItems ? pageIndex * pageSize + (totalItems > 0 ? 1 : 0) : pageIndex * pageSize + (data.length ? 1 : 0);
  const endItem = totalItems
    ? Math.min((pageIndex + 1) * pageSize, totalItems)
    : pageIndex * pageSize + data.length;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        {!hideSearch && (
          <Form.Control
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPageIndex(0);
            }}
            style={{ maxWidth: '300px' }}
          />
        )}

        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="secondary" id="column-visibility-dropdown">
            Columns
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="px-2"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            {table.getAllLeafColumns().map(column => (
              <div
                key={column.id}
                onClick={() => column.toggleVisibility()}
                className="d-flex align-items-center gap-2 py-1 px-2 dropdown-item"
                style={{ cursor: 'pointer' }}
              >
                <Form.Check
                  type="checkbox"
                  checked={column.getIsVisible()}
                  readOnly
                  onClick={(e) => e.stopPropagation()}
                />
                <span>{column.columnDef.header}</span>
              </div>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => setColumnVisibility({})}>
              Reset Columns
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
        <Form.Select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPageIndex(0);
          }}
          style={{ width: 'auto' }}
        >
          {[5, 10, 20, 50].map(size => (
            <option key={size} value={size}>Show {size}</option>
          ))}
        </Form.Select>

        <div className="text-muted small">
          {totalItems != null
            ? <>Showing <strong>{startItem}</strong>–<strong>{endItem}</strong> of <strong>{totalItems.toLocaleString()}</strong> items</>
            : <>Showing <strong>{startItem}</strong>–<strong>{endItem}</strong></>}
        </div>

        {renderPagination()}
      </div>

      <br />

      {isLoading ? (
        <ActivityIndicator />
      ) : (
        <Table striped bordered hover responsive className="mb-0">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
});

export default DataTable;
